import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import { MovableModal } from '@flipster/features/src/shared/movable-modal';
import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { addPositionModalBunja } from '../state';

const ReviewStep = () => {
  const transPositionAddPosition = useTranslations('Position_add_position.r250401');
  const transPosition = useTranslations('Position.r250401');
  const {
    positionAtom,
    existingTotalValueAtom,
    existingLiquidationPriceAtom,
    existingEntryPriceAtom,
    addingTotalValueAtom,
    addingLiquidationPriceAtom,
    addingEntryPriceAtom,
    estTotalValueAtom,
    estLiquidationPriceAtom,
    estEntryPriceAtom,
    instrumentAtom,
    targetPrecisionAtom,
    targetUnitAtom,
  } = useBunja(addPositionModalBunja);

  const position = useAtomValue(positionAtom);
  const instrument = useAtomValue(instrumentAtom);
  // total value
  const existingTotalValue = useAtomValue(existingTotalValueAtom);
  const addingTotalValue = useAtomValue(addingTotalValueAtom);
  const estTotalValue = useAtomValue(estTotalValueAtom);
  // liquidation price
  const existingLiquidationPrice = useAtomValue(existingLiquidationPriceAtom);
  const addingLiquidationPrice = useAtomValue(addingLiquidationPriceAtom);
  const estLiquidationPrice = useAtomValue(estLiquidationPriceAtom);
  // entry price
  const existingEntryPrice = useAtomValue(existingEntryPriceAtom);
  const addingEntryPrice = useAtomValue(addingEntryPriceAtom);
  const estEntryPrice = useAtomValue(estEntryPriceAtom);

  const estPrecision = useAtomValue(targetPrecisionAtom);
  const estSuffix = useAtomValue(targetUnitAtom);

  const items = [
    {
      key: 'totalValue',
      label: transPosition('Est_total_value'),
      value: (
        <Formatted.Number
          value={estTotalValue}
          suffix={estSuffix}
          toFixed
          precision={estPrecision}
          css={{ textStyle: 'Text/L/Monospace/Strong' }}
        />
      ),
      existingValue: (
        <Formatted.Number
          value={existingTotalValue}
          suffix={estSuffix}
          toFixed
          precision={estPrecision}
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
      addingValue: (
        <Formatted.Number
          value={addingTotalValue}
          prefix='+ '
          suffix={estSuffix}
          toFixed
          precision={estPrecision}
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
    },
    {
      key: 'liqPrice',
      label:
        position?.marginType === 'Cross' ? (
          <Tooltip.Root placement='bottom-start'>
            <Tooltip.Trigger asChild>
              <span
                className={css({
                  borderBottomWidth: 1,
                  borderBottomStyle: 'dashed',
                  borderBottomColor: 'border.default.level-3',
                })}
              >
                <T id='Position.r250401.Est_liquidation_price' />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}>
              <T id='Trade.r260506.Estimated_liquidation_price_desc' />
            </Tooltip.Content>
          </Tooltip.Root>
        ) : (
          transPosition('Est_liquidation_price')
        ),
      value: (
        <Formatted.Number
          value={estLiquidationPrice ?? '0'}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          zeroString='-'
          css={{ textStyle: 'Text/L/Monospace/Strong' }}
        />
      ),
      existingValue: (
        <Formatted.Number
          value={existingLiquidationPrice ?? '0'}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          zeroString='-'
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
      addingValue: (
        <Formatted.Number
          value={addingLiquidationPrice ?? '0'}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          zeroString='-'
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
    },
    {
      key: 'entryPrice',
      label: transPosition('Est_entry_price'),
      value: (
        <Formatted.Number
          value={estEntryPrice}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          css={{ textStyle: 'Text/L/Monospace/Strong' }}
        />
      ),
      existingValue: (
        <Formatted.Number
          value={existingEntryPrice}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
      addingValue: (
        <Formatted.Number
          value={addingEntryPrice}
          suffix={instrument.quoteCurrency}
          toFixed
          precision={instrument.pricePrecision}
          css={{ textStyle: 'Text/L/Monospace/Regular' }}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <MovableModal.Header>
        <MovableModal.Title label={transPositionAddPosition('Review_title')} />
      </MovableModal.Header>
      <MovableModal.Content>
        {items.map(({ key, ...item }) => (
          <ReviewItem key={key} {...item} />
        ))}
      </MovableModal.Content>
    </>
  );
};

interface ReviewItemProps {
  label: ReactNode;
  value: ReactNode;
  existingValue?: ReactNode;
  addingValue?: ReactNode;
}

const ReviewItem = ({ label, value, existingValue, addingValue }: ReviewItemProps) => {
  const transPositionAddPosition = useTranslations('Position_add_position.r250401');
  return (
    <div className={stack({ gap: 12, '& + &': { mt: 24 } })}>
      <div
        className={hstack({
          p: 12,
          rounded: 8,
          bg: 'background.default.level-3',
          justify: 'space-between',
          color: 'content.default.level-5',
        })}
      >
        <span>{label}</span>
        {value}
      </div>
      <div className={stack({ px: 12, gap: 8 })}>
        {existingValue && (
          <div className={hstack({ justify: 'space-between' })}>
            <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>
              {transPositionAddPosition('Existing_label')}
            </span>
            {existingValue}
          </div>
        )}
        {addingValue && (
          <div className={hstack({ justify: 'space-between' })}>
            <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>
              {transPositionAddPosition('Adding_label')}
            </span>
            {addingValue}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewStep;
